/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  Component,
  forwardRef,
  Input
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import * as _ from 'lodash';
import template from './iab-multi-select.component.html';
import {FORM_CONTROL_IMPORTS} from './form-control-imports';

import {AdnSelectSearchElement} from '../adn-select/adn-select.component';
import {
  normalizeIabCategoryIds,
  PreparedIabCategory,
  searchIabCategories,
  seedIabSearchResults
} from '../../../../components/util/iab-category-search';

function coerceBooleanAttr(value: any, defaultValue = false): boolean {
  if (value === true || value === 'true') {
    return true;
  }
  if (value === false || value === 'false') {
    return false;
  }
  if (value === '') {
    return true;
  }
  return defaultValue;
}

@Component({
  standalone: true,
  imports: FORM_CONTROL_IMPORTS,
  selector: 'adn-iab-multi-select',
  template,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => IabMultiSelectComponent),
    multi: true
  }]
})
export class IabMultiSelectComponent implements ControlValueAccessor {
  @Input() fieldName?: string;
  @Input() placeholder = 'Select IAB Category';
  @Input() changeHook?: () => void;

  @Input() set allowClear(value: boolean | string | undefined) {
    this._allowClear = coerceBooleanAttr(value, true);
  }
  get allowClear(): boolean {
    return this._allowClear;
  }

  categoryIds: string[] = [];
  searchResults: PreparedIabCategory[] = [];

  private _allowClear = true;
  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  writeValue(value: any): void {
    this.setCategoryIds(normalizeIabCategoryIds(value));
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  iabSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchResults = searchIabCategories(searchElement.search || '');
  };

  onSelectionChange(categoryIds: string[]): void {
    const nextIds = normalizeIabCategoryIds(categoryIds);
    if (_.isEqual(nextIds, this.categoryIds)) {
      return;
    }

    this.setCategoryIds(nextIds);
    this.notifyFormEdited();
    this.onChange(nextIds.slice());
    this.onTouched();
  }

  private notifyFormEdited(): void {
    if (_.isFunction(this.changeHook)) {
      this.changeHook();
    }
  }

  private setCategoryIds(categoryIds: string[]): void {
    if (_.isEqual(categoryIds, this.categoryIds)) {
      return;
    }
    this.categoryIds = categoryIds;
    this.searchResults = seedIabSearchResults(this.searchResults, categoryIds);
  }
}
